import React, { useState, useEffect } from 'react';
import {
  Building2,
  Plus,
  Search,
  MapPin,
  Phone,
  Mail,
  User,
  ShieldCheck,
  ChevronRight,
  Filter,
  Navigation,
  Compass,
  CheckCircle2,
} from 'lucide-react';
import { BranchItem } from '../types/index.ts';

interface BranchesPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const BranchesPage: React.FC<BranchesPageProps> = ({ onNavigate }) => {
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('ALL');
  const [showAddModal, setShowAddModal] = useState(false);

  // GPS Pin Capture Modal
  const [calibratingBranch, setCalibratingBranch] = useState<BranchItem | null>(null);
  const [gpsForm, setGpsForm] = useState({
    latitude: '',
    longitude: '',
    allowedRadiusMeters: 150,
  });
  const [isCapturingGps, setIsCapturingGps] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [form, setForm] = useState({
    branchCode: '',
    branchName: '',
    city: 'Karachi',
    region: 'South Region',
    address: '',
    contactPerson: '',
    phone: '',
    email: '',
    ublEngineer: '',
    assignedTeam: '',
  });

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/branches');
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setBranches(data);
        }
      }
    } catch (err) {
      console.warn('Branches fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/branches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, customerId: 1 }),
      });
      if (res.ok) {
        setShowAddModal(false);
        setForm({
          branchCode: '',
          branchName: '',
          city: 'Karachi',
          region: 'South Region',
          address: '',
          contactPerson: '',
          phone: '',
          email: '',
          ublEngineer: '',
          assignedTeam: '',
        });
        fetchBranches();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = (Array.isArray(branches) ? branches : []).filter((b) => {
    const matchesSearch =
      (b.branchCode || '').toLowerCase().includes(search.toLowerCase()) ||
      (b.branchName || '').toLowerCase().includes(search.toLowerCase()) ||
      (b.city || '').toLowerCase().includes(search.toLowerCase()) ||
      (b.ublEngineer && b.ublEngineer.toLowerCase().includes(search.toLowerCase()));

    const matchesRegion = selectedRegion === 'ALL' || (b.region || '').toLowerCase().includes(selectedRegion.toLowerCase());

    return matchesSearch && matchesRegion;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              UBL Branches Directory
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-200">
              Customer: UBL
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Hierarchy: United Bank Limited (UBL) → Region → City → Branch → Projects &amp; Tickets
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors self-start sm:self-auto"
        >
          <Plus className="w-4 h-4 text-amber-400" />
          <span>Add UBL Branch</span>
        </button>
      </div>

      {/* Region Hierarchy Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div
          onClick={() => setSelectedRegion('South')}
          className={`p-4 rounded-xl border cursor-pointer transition-all ${
            selectedRegion === 'South'
              ? 'bg-blue-50/80 border-blue-400 shadow-xs'
              : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">South Region</div>
          <div className="text-lg font-bold text-slate-900 mt-1">Karachi &amp; Sindh Hub</div>
          <p className="text-xs text-slate-500 mt-0.5">Corporate Main, Clifton, Kehkashan</p>
        </div>

        <div
          onClick={() => setSelectedRegion('Central')}
          className={`p-4 rounded-xl border cursor-pointer transition-all ${
            selectedRegion === 'Central'
              ? 'bg-blue-50/80 border-blue-400 shadow-xs'
              : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Central Region</div>
          <div className="text-lg font-bold text-slate-900 mt-1">Lahore &amp; Punjab Hub</div>
          <p className="text-xs text-slate-500 mt-0.5">Gulberg III, Mall Road, Faisalabad</p>
        </div>

        <div
          onClick={() => setSelectedRegion('North')}
          className={`p-4 rounded-xl border cursor-pointer transition-all ${
            selectedRegion === 'North'
              ? 'bg-blue-50/80 border-blue-400 shadow-xs'
              : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">North Region</div>
          <div className="text-lg font-bold text-slate-900 mt-1">Islamabad &amp; KPK Hub</div>
          <p className="text-xs text-slate-500 mt-0.5">Blue Area, Saddar Rawalpindi, Peshawar</p>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200">
        <div className="flex items-center gap-2 w-full sm:max-w-md">
          <Search className="w-4 h-4 text-slate-400 ml-1" />
          <input
            type="text"
            className="w-full text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden"
            placeholder="Filter branch code (e.g. UBL-0142), branch name, city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <span className="text-xs text-slate-500 font-medium">Region:</span>
          <select
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-slate-50 text-slate-700 focus:outline-hidden"
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
          >
            <option value="ALL">All Regions</option>
            <option value="South">South Region</option>
            <option value="Central">Central Region</option>
            <option value="North">North Region</option>
          </select>
        </div>
      </div>

      {/* Branches Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Branch Code &amp; Name</th>
                <th className="py-3 px-4">City / Region</th>
                <th className="py-3 px-4">Address</th>
                <th className="py-3 px-4">UBL Field Engineer</th>
                <th className="py-3 px-4">Assigned NB Team</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-3 px-4">
                    <div className="font-bold text-blue-900">{b.branchCode}</div>
                    <div className="font-semibold text-slate-800">{b.branchName}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {b.latitude && b.longitude ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                          TEST GPS / NEEDS VERIFICATION
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600">
                          GPS NOT PINNED
                        </span>
                      )}
                      <span className="text-[10px] text-slate-400 font-mono">
                        Radius: {b.allowedRadiusMeters || 150}m
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-semibold text-slate-800">{b.city}</div>
                    <div className="text-[11px] text-slate-500">{b.region}</div>
                  </td>
                  <td className="py-3 px-4 max-w-xs truncate text-slate-600">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                      <span className="truncate">{b.address}</span>
                    </div>
                    {b.latitude && b.longitude && (
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                        Pin: {b.latitude}, {b.longitude}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-medium text-slate-800">{b.ublEngineer || 'Assigned per Project'}</div>
                    {b.phone && <div className="text-[11px] text-slate-400">{b.phone}</div>}
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-block px-2 py-0.5 bg-slate-100 rounded text-[11px] font-medium text-slate-700">
                      {b.assignedTeam || 'Naeem Builder Alpha'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => {
                          setCalibratingBranch(b);
                          setGpsForm({
                            latitude: b.latitude || '',
                            longitude: b.longitude || '',
                            allowedRadiusMeters: b.allowedRadiusMeters || 150,
                          });
                        }}
                        className="text-xs font-semibold text-emerald-800 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded transition-colors inline-flex items-center gap-1"
                      >
                        <Compass className="w-3 h-3 text-emerald-600" />
                        Capture Pin
                      </button>
                      <button
                        onClick={() => onNavigate('tickets')}
                        className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 px-2.5 py-1 rounded transition-colors"
                      >
                        Tickets
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Branch Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Add New UBL Branch</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Branch Code *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. UBL-0589"
                    className="w-full border border-slate-200 rounded-lg p-2 focus:ring-1 focus:ring-blue-500"
                    value={form.branchCode}
                    onChange={(e) => setForm({ ...form, branchCode: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Branch Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. UBL Tariq Road Branch"
                    className="w-full border border-slate-200 rounded-lg p-2 focus:ring-1 focus:ring-blue-500"
                    value={form.branchName}
                    onChange={(e) => setForm({ ...form, branchName: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">City *</label>
                  <input
                    type="text"
                    required
                    className="w-full border border-slate-200 rounded-lg p-2 focus:ring-1 focus:ring-blue-500"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Region *</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2 focus:ring-1 focus:ring-blue-500"
                    value={form.region}
                    onChange={(e) => setForm({ ...form, region: e.target.value })}
                  >
                    <option value="South Region">South Region (Karachi &amp; Sindh)</option>
                    <option value="Central Region">Central Region (Lahore &amp; Punjab)</option>
                    <option value="North Region">North Region (Islamabad, Rawalpindi &amp; KPK)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Address *</label>
                <textarea
                  required
                  rows={2}
                  placeholder="Full plot/street address"
                  className="w-full border border-slate-200 rounded-lg p-2 focus:ring-1 focus:ring-blue-500"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Contact Person / BM</label>
                  <input
                    type="text"
                    placeholder="Branch Manager Name"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.contactPerson}
                    onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Phone</label>
                  <input
                    type="text"
                    placeholder="+92 21 ..."
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">UBL Premises Engineer</label>
                  <input
                    type="text"
                    placeholder="e.g. Engr. Salman Rauf"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.ublEngineer}
                    onChange={(e) => setForm({ ...form, ublEngineer: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Assigned Naeem Builder Team</label>
                  <input
                    type="text"
                    placeholder="e.g. Team Alpha (Engr. Bilal)"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={form.assignedTeam}
                    onChange={(e) => setForm({ ...form, assignedTeam: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold"
                >
                  Save Branch
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Capture Precise GPS Pin Modal */}
      {calibratingBranch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-md w-full p-5 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Compass className="w-4 h-4 text-emerald-700" />
                <h3 className="font-bold text-slate-900 text-sm">
                  Capture Precise GPS Pin: {calibratingBranch.branchCode}
                </h3>
              </div>
              <button
                onClick={() => setCalibratingBranch(null)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const res = await fetch(`/api/branches/${calibratingBranch.id}/gps`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(gpsForm),
                  });
                  const data = await res.json();
                  if (res.ok) {
                    setToastMessage(data.message || 'GPS Pin updated successfully!');
                    setCalibratingBranch(null);
                    await fetchBranches();
                  } else {
                    setToastMessage(data.error || 'Failed to update GPS pin');
                  }
                } catch (err: any) {
                  setToastMessage(err.message || 'Error updating GPS pin');
                }
              }}
              className="space-y-3.5 text-xs"
            >
              <div className="p-3 bg-amber-50/80 border border-amber-200 rounded-lg text-amber-900">
                <span className="font-bold block">Current Benchmark Status:</span>
                <span className="text-[11px] text-amber-800">
                  {calibratingBranch.latitude && calibratingBranch.longitude
                    ? 'Approximate / Test Coordinates. Needs physical on-site pinning.'
                    : 'Unconfigured. Technicians cannot be geo-fenced until calibrated.'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-700">Surveyed Satellite Coordinates</span>
                <button
                  type="button"
                  onClick={() => {
                    if (!navigator.geolocation) {
                      setToastMessage('Geolocation is not supported by your browser.');
                      return;
                    }
                    setIsCapturingGps(true);
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        setGpsForm((prev) => ({
                          ...prev,
                          latitude: pos.coords.latitude.toFixed(6),
                          longitude: pos.coords.longitude.toFixed(6),
                        }));
                        setIsCapturingGps(false);
                      },
                      (err) => {
                        setIsCapturingGps(false);
                        setToastMessage(`Hardware GPS warning: ${err.message}`);
                      }
                    );
                  }}
                  className="text-xs font-bold text-emerald-700 hover:underline flex items-center gap-1"
                >
                  <Navigation className={`w-3.5 h-3.5 ${isCapturingGps ? 'animate-spin' : ''}`} />
                  {isCapturingGps ? 'Acquiring...' : 'Acquire Current Pin'}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Latitude</label>
                  <input
                    type="text"
                    required
                    placeholder="24.8214"
                    value={gpsForm.latitude}
                    onChange={(e) => setGpsForm({ ...gpsForm, latitude: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded text-xs font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Longitude</label>
                  <input
                    type="text"
                    required
                    placeholder="67.0321"
                    value={gpsForm.longitude}
                    onChange={(e) => setGpsForm({ ...gpsForm, longitude: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded text-xs font-mono font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                  Allowed Perimeter Radius (Meters) *
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    required
                    min={50}
                    max={1000}
                    value={gpsForm.allowedRadiusMeters}
                    onChange={(e) => setGpsForm({ ...gpsForm, allowedRadiusMeters: parseInt(e.target.value) || 150 })}
                    className="w-28 px-2.5 py-1.5 border border-slate-200 rounded text-xs font-mono font-bold"
                  />
                  <span className="text-[11px] text-slate-500">
                    Default 150m (Configurable per branch layout)
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setCalibratingBranch(null)}
                  className="px-3.5 py-1.5 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold"
                >
                  Save Precise Pin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
